import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useProduct } from '../hooks/useProduct';
import { useCart } from '../contexts/CartContext';
import { CartItem } from '../types/cart';
import BackHeader from '../components/BackHeader';
import NavigationBar from '../components/NavigationBar';
import LoadingState from '../components/LoadingState';
import ErrorState from '../components/ErrorState';
import ImageWithFallback from '../components/ImageWithFallback';
import ServiceClosedModal from '../components/ServiceClosedModal';
import CartBottomSheet from '../components/CartBottomSheet';
import { getStatusMessages } from '../config/constants';
import { getLocalizedValue, getCurrentLanguage } from '../config/languageConfig';
import { 
  formatPrice as formatPriceWithCurrency, 
  getCurrencyFromLanguage,
  getPriceFieldByCurrency
} from '../utils/currencyUtils';
import { useOperationTimeCheck } from '../hooks/useOperationTime';
import { useAddressAffordance } from '../contexts/AddressAffordanceContext';
import useTranslation from '../hooks/useTranslation';

const ProductDetail: React.FC = () => {
  const { shopId, productId } = useParams<{ shopId: string; productId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { cart, addToCart, updateItemQuantity, canceledItemId } = useCart();
  const [showServiceClosedModal, setShowServiceClosedModal] = useState(false);
  const { checkAndExecute } = useOperationTimeCheck();
  const { checkAddressAndExecute } = useAddressAffordance();

  // 현재 언어 및 통화 설정
  const currentLang = getCurrentLanguage();
  const selectedCurrency = getCurrencyFromLanguage(currentLang);

  // 다국어 상태 메시지
  const STATUS_MESSAGES = getStatusMessages();

  // 상품 정보 가져오기
  const { product, loading, error, refetch } = useProduct(productId || '');

  // 현재 언어에 맞는 상품명 가져오기
  const displayName = useMemo(() => {
    if (!product) return '';
    return getLocalizedValue(product.productNameKo, {
      en: product.productNameEn,
      ja: product.productNameJa,
      'zh-CN': product.productNameZhCn,
      'zh-TW': product.productNameZhTw
    });
  }, [product]);

  // 현재 언어에 맞는 상품 설명 가져오기
  const displayDescription = useMemo(() => {
    if (!product) return '';
    return getLocalizedValue(product.productDescriptionKo || '', {
      en: product.productDescriptionEn || '',
      ja: product.productDescriptionJa || '',
      'zh-CN': product.productDescriptionZhCn || '',
      'zh-TW': product.productDescriptionZhTw || ''
    });
  }, [product]);

  // 현재 언어에 맞는 기본 정보 가져오기
  const displayBasicInfo = useMemo(() => {
    if (!product) return '';
    return getLocalizedValue(product.basicInfoKo || '', {
      en: product.basicInfoEn || '',
      ja: product.basicInfoJa || '',
      'zh-CN': product.basicInfoZhCn || '',
      'zh-TW': product.basicInfoZhTw || ''
    });
  }, [product]);

  // 현재 언어에 맞는 추가 정보 가져오기
  const displayAdditionalInfo = useMemo(() => {
    if (!product) return '';
    return getLocalizedValue(product.additionalInfoKo || '', {
      en: product.additionalInfoEn || '',
      ja: product.additionalInfoJa || '',
      'zh-CN': product.additionalInfoZhCn || '',
      'zh-TW': product.additionalInfoZhTw || ''
    });
  }, [product]);

  // 상품에서 현재 언어에 맞는 가격 필드 선택
  const getItemPrice = useCallback((): number => {
    if (!product) return 0;
    const currency = getCurrencyFromLanguage(currentLang);
    const priceField = getPriceFieldByCurrency(currency);
    
    switch (priceField) {
      case 'priceKrw':
        return product.priceKrw;
      case 'priceUsd':
        return product.priceUsd;
      case 'priceJpy':
        return product.priceJpy;
      case 'priceCny':
        return product.priceCny;
      case 'priceTwd':
        return product.priceTwd;
      default:
        return product.price;
    }
  }, [product, currentLang]);

  // 현재 언어에 맞는 가격 계산
  const currentPrice = getItemPrice();

  // 가격 표시
  const displayPrice = formatPriceWithCurrency(currentPrice, selectedCurrency);

  // 장바구니에서 현재 아이템의 수량 찾기
  const getCartQuantity = useCallback((): number => {
    if (!cart || !product) return 0;
    
    // 현재 표시중인 기프트샵과 카트에 저장된 레스토랑이 다르면 0 반환
    if (cart.restaurantId !== shopId) return 0;
    
    const cartItem = cart.items.find(item => item.id === product.id);
    return cartItem ? cartItem.quantity : 0;
  }, [cart, product, shopId]);

  // 현재 수량 상태 관리
  const [quantity, setQuantity] = useState<number>(0);

  // 카트 상태가 변경될 때마다 수량 동기화
  useEffect(() => {
    setQuantity(getCartQuantity());
  }, [cart, getCartQuantity]);

  // 취소된 아이템 감지하여 수량 초기화
  useEffect(() => {
    if (canceledItemId && product && canceledItemId === product.id) {
      setQuantity(getCartQuantity());
    }
  }, [canceledItemId, product, getCartQuantity]);

  // 수량 증가
  const increaseQuantity = () => {
    checkAddressAndExecute(() => {
      checkAndExecute(
        () => {
          let startQuantity = quantity;
          if (cart && cart.restaurantId !== shopId && quantity === 0) {
            startQuantity = 0;
          }
          
          const newQuantity = startQuantity + 1;
          setQuantity(newQuantity);
          updateCartItem(newQuantity);
        },
        () => setShowServiceClosedModal(true)
      );
    }, 'cart');
  };

  // 수량 감소
  const decreaseQuantity = () => {
    if (quantity > 0) {
      checkAndExecute(
        () => {
          const newQuantity = quantity - 1;
          setQuantity(newQuantity);
          updateCartItem(newQuantity);
        },
        () => setShowServiceClosedModal(true)
      );
    }
  };

  // 장바구니 업데이트
  const updateCartItem = (newQuantity: number) => {
    if (!product || !shopId) return;

    // 수량이 0이면 아이템 제거
    if (newQuantity === 0) {
      updateItemQuantity(product.id, 0);
    } 
    // 처음 추가하는 경우
    else if (quantity === 0 && newQuantity > 0) {
      // 장바구니 아이템 생성
      const cartItem: CartItem = {
        id: product.id,
        name: displayName,
        price: currentPrice,
        quantity: newQuantity,
        options: [],
        image: product.imageUrl,
        nameEn: product.productNameEn || undefined,
        priceKrw: product.priceKrw,
        priceUsd: product.priceUsd,
        priceJpy: product.priceJpy,
        priceCny: product.priceCny,
        priceTwd: product.priceTwd
      };
      
      // 가게 정보 (기프트샵 고정)
      const shopInfo = {
        restaurantName: 'Gift Shop',
        restaurantNameEn: 'Gift Shop',
        restaurantNameJa: 'Gift Shop',
        restaurantNameZhCn: 'Gift Shop',
        restaurantNameZhTw: 'Gift Shop',
        restaurantNameKo: '기념품샵'
      };
      
      addToCart(shopId, shopInfo, cartItem);
    } 
    // 기존 아이템 수량 업데이트
    else if (cart && cart.restaurantId === shopId) {
      updateItemQuantity(product.id, newQuantity);
    }
  };

  // 카트에 추가하고 기프트샵으로 이동
  const addToCartHandler = () => {
    if (quantity === 0) {
      increaseQuantity();
    } else {
      updateCartItem(quantity);
      navigate(`/restaurant/${shopId}`);
    }
  };

  // 장바구니 페이지로 이동
  const handleCheckout = () => {
    checkAddressAndExecute(() => {
      checkAndExecute(
        () => navigate('/cart'),
        () => setShowServiceClosedModal(true)
      );
    });
  };

  // 섹션 펼치기/접기 상태 관리
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(true);
  const [isBasicInfoExpanded, setIsBasicInfoExpanded] = useState(false);
  const [isAdditionalInfoExpanded, setIsAdditionalInfoExpanded] = useState(false);

  // shopId나 productId가 없는 경우
  if (!shopId || !productId) {
    return (
      <div className="min-h-screen bg-gray-50">
        <BackHeader title={t('error.title')} />
        <div className="p-4">
          <ErrorState message={t('product.invalidId')} onRetry={() => window.location.reload()} />
        </div>
        <NavigationBar />
      </div>
    );
  }

  // 로딩 중 표시
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <BackHeader title={displayName || t('product.item')} />
        <div className="p-4">
          <LoadingState message={STATUS_MESSAGES.loading.menus} />
        </div>
        <NavigationBar />
      </div>
    );
  }

  // 에러 표시
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <BackHeader title={t('error.title')} />
        <div className="p-4">
          <ErrorState message={error} onRetry={refetch} />
        </div>
        <NavigationBar />
      </div>
    );
  }

  // 상품 정보가 없을 때
  if (!product) {
    return (
      <div className="min-h-screen bg-gray-50">
        <BackHeader title={t('error.notFound')} />
        <div className="p-4">
          <ErrorState message={t('product.notFound')} onRetry={refetch} />
        </div>
        <NavigationBar />
      </div>
    );
  }

  return (
    <>
      <div className="min-h-screen bg-gray-50 flex flex-col">
        {/* Header */}
        <BackHeader title={displayName} />

        <div className="flex-grow overflow-auto pb-40">
          {/* 상품 이미지 */}
          <div className="w-full h-56 bg-gray-200 relative">
            <ImageWithFallback
              src={product.imageUrl || ''}
              alt={displayName}
              className="w-full h-full object-cover"
              fallback="https://source.unsplash.com/random/400x320/?gift"
            />
          </div>

          {/* 상품 정보 */}
          <div className="p-4 bg-white rounded-t-xl -mt-4 relative z-10 shadow-sm">
            <h1 className="text-xl font-bold mb-2 pt-2">{displayName}</h1>
            <div className="text-xl font-bold text-red-500 mb-6">{displayPrice}</div>
           
            {/* 수량 조절 UI */}
            <div className="py-4 border-t border-b border-gray-200">
              <div className="flex items-center justify-between">
                <span className="text-gray-800 font-semibold text-lg">{t('quantity')}</span>
                
                <div className="flex items-center">
                  <button
                    className={`w-8 h-8 flex items-center justify-center border rounded-md ${
                      quantity > 0 ? 'border-gray-300 text-gray-700' : 'border-gray-200 text-gray-400'
                    }`}
                    onClick={decreaseQuantity}
                    disabled={quantity === 0}
                    aria-label="Decrease quantity"
                  >
                    <span className="text-xl font-medium">−</span>
                  </button>
                  <span className="mx-4 text-lg font-medium min-w-[20px] text-center">{quantity}</span>
                  <button
                    className="w-8 h-8 flex items-center justify-center border rounded-md bg-red-500 text-white border-red-500"
                    onClick={increaseQuantity}
                    aria-label="Increase quantity"
                  >
                    <span className="text-xl font-medium">+</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
           
            {/* 상품 설명 */}
            {displayDescription && (
              <div className="mb-2 border border-gray-200 rounded-lg overflow-hidden">
                <button
                  onClick={() => setIsDescriptionExpanded(!isDescriptionExpanded)}
                  className="w-full flex items-center justify-between p-4 text-left bg-white hover:bg-gray-50 transition-colors duration-200 focus:outline-none focus:bg-gray-50"
                >
                  <h3 className="text-base font-medium text-gray-900">{t('product.description')}</h3>
                  <svg
                    className={`w-5 h-5 text-gray-500 transition-transform duration-200 ${
                      isDescriptionExpanded ? 'rotate-180' : ''
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {isDescriptionExpanded && (
                  <div className="px-4 py-4 pb-4 bg-gray-50 border-t border-gray-100">
                    <div className="text-gray-800 text-sm leading-6 whitespace-pre-line text-left">
                      {displayDescription}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* 기본 정보 */}
            {displayBasicInfo && (
              <div className="mb-2 border border-gray-200 rounded-lg overflow-hidden">
                <button
                  onClick={() => setIsBasicInfoExpanded(!isBasicInfoExpanded)}
                  className="w-full flex items-center justify-between p-4 text-left bg-white hover:bg-gray-50 transition-colors duration-200 focus:outline-none focus:bg-gray-50"
                >
                  <h3 className="text-base font-medium text-gray-900">{t('product.basicInfo')}</h3>
                  <svg
                    className={`w-5 h-5 text-gray-500 transition-transform duration-200 ${
                      isBasicInfoExpanded ? 'rotate-180' : ''
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {isBasicInfoExpanded && (
                  <div className="px-4 py-4 pb-4 bg-gray-50 border-t border-gray-100">
                    <div className="text-gray-800 text-sm leading-6 whitespace-pre-line text-left">
                      {displayBasicInfo}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* 추가 정보 */}
            {displayAdditionalInfo && (
              <div className="mb-4 border border-gray-200 rounded-lg overflow-hidden">
                <button
                  onClick={() => setIsAdditionalInfoExpanded(!isAdditionalInfoExpanded)}
                  className="w-full flex items-center justify-between p-4 text-left bg-white hover:bg-gray-50 transition-colors duration-200 focus:outline-none focus:bg-gray-50"
                >
                  <h3 className="text-base font-medium text-gray-900">{t('product.additionalInfo')}</h3>
                  <svg
                    className={`w-5 h-5 text-gray-500 transition-transform duration-200 ${
                      isAdditionalInfoExpanded ? 'rotate-180' : ''
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {isAdditionalInfoExpanded && (
                  <div className="px-4 py-4 pb-4 bg-gray-50 border-t border-gray-100">
                    <div className="text-gray-800 text-sm leading-6 whitespace-pre-line text-left">
                      {displayAdditionalInfo}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* 하단 여백 추가 */}
            <div className="h-32"></div>

        </div>

        {/* 하단 액션 영역 */}
        <div className="fixed bottom-16 left-1/2 z-20 w-full max-w-mobile -translate-x-1/2 transform border-t border-gray-100 bg-white p-4 shadow-top">
          <div className="w-full flex items-center justify-between">
            <div className="mr-4">
              <div className="text-gray-600 text-sm">{t('menu.totalPrice')}</div>
              <div className="text-xl font-bold">{formatPriceWithCurrency(currentPrice * quantity, selectedCurrency)}</div>
            </div>
            
            {quantity > 0 ? (
              <button
                className="flex-1 py-3 px-4 rounded-lg font-semibold text-white bg-red-500"
                onClick={addToCartHandler}
              >
                {t('product.goToShop')}
              </button>
            ) : (
              <button
                className="flex-1 py-3 px-4 rounded-lg font-semibold text-white bg-red-500"
                onClick={addToCartHandler}
              >
                {t('product.addToCart')}
              </button>
            )}
          </div>
        </div>

        {/* 푸터 탐색 바 */}
        <div className="h-16" />
        <NavigationBar />
      </div>

      {/* 카트 정보 */}
      <CartBottomSheet
        minOrderAmount={null}
        onCheckout={handleCheckout}
        restaurantId={shopId || ''}
        currency={selectedCurrency}
      />

      {/* 운영시간 외 모달 */}
      <ServiceClosedModal 
        isOpen={showServiceClosedModal}
        onClose={() => setShowServiceClosedModal(false)}
      />
    </>
  );
};

export default ProductDetail; 